import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Users, UserCheck, Gift } from "lucide-react";
import { PageHeader, Loader, StatCard, Badge, Empty } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { fmtPKR, fmtDate, statusColor } from "@/lib/format";

export default function Referral() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.get("/referral").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <Loader />;

  const copy = (text) => { navigator.clipboard.writeText(text); setCopied(true); toast.success(t("copied")); setTimeout(() => setCopied(false), 1500); };

  return (
    <div>
      <PageHeader title={t("referral")} subtitle="Invite friends and earn 10% of your referred friend's first approved deposit." />
      <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
        <StatCard icon={Users} label={t("total_referrals")} value={data.total_referrals} accent="blue" testid="stat-total-ref" />
        <StatCard icon={UserCheck} label={t("active_referrals")} value={data.active_referrals} accent="green" testid="stat-active-ref" />
        <StatCard icon={Gift} label={t("referral_rewards")} value={fmtPKR(data.referral_rewards)} accent="purple" testid="stat-ref-rewards" />
      </div>

      <div className="ec-card p-6 mb-8">
        <p className="text-sm text-gray-400 mb-2">{t("your_ref_code")}</p>
        <div className="flex flex-wrap gap-3 items-center">
          <span className="font-head text-2xl font-extrabold text-gradient keep-ltr" data-testid="referral-code">{data.referral_code}</span>
          <Button size="sm" onClick={() => copy(data.referral_code)} className="bg-white/5 hover:bg-white/10 border border-white/10" data-testid="copy-code-button">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</Button>
        </div>
        <p className="text-sm text-gray-400 mt-5 mb-2">{t("ref_link")}</p>
        <div className="flex gap-2">
          <Input readOnly value={data.referral_link} className="keep-ltr text-xs" data-testid="referral-link" />
          <Button onClick={() => copy(data.referral_link)} className="bg-orange-600 hover:bg-orange-500 shrink-0" data-testid="copy-link-button"><Copy className="w-4 h-4 me-1" />{t("copy")}</Button>
        </div>
      </div>

      <h3 className="font-head text-lg font-semibold text-white mb-4">{t("ref_history")}</h3>
      {data.history.length === 0 ? <Empty label="No referrals yet." /> : (
        <div className="ec-card divide-y divide-white/5">
          {data.history.map((r) => (
            <div key={r.id} className="flex items-center justify-between px-5 py-4" data-testid={`ref-${r.id}`}>
              <div><p className="text-sm text-white">{r.referred_name}</p><p className="text-xs text-gray-500">{fmtDate(r.created_at)}</p></div>
              <div className="flex items-center gap-3">
                <Badge className={statusColor(r.status)}>{r.status}</Badge>
                <span className="text-emerald-400 font-semibold text-sm">{fmtPKR(r.reward)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {data.tree && (
        <div className="mt-8">
          <h3 className="font-head text-lg font-semibold text-white mb-4">Complete Referral Tree</h3>
          <div className="ec-card p-6" data-testid="referral-tree">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center font-bold uppercase">{(data.tree.name || "M")[0]}</div>
              <div><p className="font-semibold text-white">{data.tree.name} (You)</p><p className="text-xs text-gray-500 keep-ltr">{data.tree.referral_code}</p></div>
            </div>
            {data.tree.level1.length === 0 ? (
              <p className="text-gray-500 text-sm mt-4 ms-12">No referrals yet — share your link to grow your tree.</p>
            ) : (
              <div className="mt-3 ms-5 ps-6 border-s border-white/10 space-y-4">
                {data.tree.level1.map((l1) => (
                  <div key={l1.id} data-testid={`tree-l1-${l1.id}`}>
                    <div className="flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-white/40" />
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold uppercase">{l1.name[0]}</div>
                      <div><p className="text-white text-sm font-medium">{l1.name}</p><p className="text-xs text-gray-500">L1 · {l1.status} · {fmtDate(l1.created_at)}</p></div>
                    </div>
                    {l1.children.length > 0 && (
                      <div className="mt-2 ms-4 ps-6 border-s border-white/5 space-y-2">
                        {l1.children.map((l2) => (
                          <div key={l2.id} className="flex items-center gap-3" data-testid={`tree-l2-${l2.id}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-white/25" />
                            <div className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-xs font-bold uppercase">{l2.name[0]}</div>
                            <div><p className="text-gray-200 text-sm">{l2.name}</p><p className="text-xs text-gray-600">L2 · {l2.status} · {fmtDate(l2.created_at)}</p></div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
